import React, { useCallback, useEffect, useRef, useState } from "react";
import { Monitor, Moon, Phone, PhoneCall, PhoneOff, Search, Sun, UserPlus, Users, X } from "lucide-react";
import {
    acceptContactCall,
    addContact,
    declineContactCall,
    getContactCall,
    getIncomingGroupCalls,
    getIncomingCalls,
    getIncomingRequests,
    getUserInfo,
    joinGroupCall,
    declineGroupCall,
    removeContact,
    searchUsers,
    startGroupCall,
    startContactCall,
} from "../services/api";
import apiRoutes from "../services/apiRoutes";
import { appPath } from "../services/appRoutes";
import { sendCallDebugLog } from "../services/callDebugLogger";
import { THEME_MODES, THEME_STORAGE_KEY, getInitialThemeMode, resolveTheme } from "../services/theme";
import GroupCallSession from "../GroupCallSession";
import WebRTCCallSession from "../WebRTCCallSession";

const themeModes = THEME_MODES;
const debugCalls = true;
const callTonesEnabled = false;

function logCallDebug(event, payload = {}) {
    if (debugCalls) {
        console.log(`[calls] ${event}`, payload);
    }
    sendCallDebugLog("calls", event, payload);
}

function startToneLoop({ pattern, intervalMs, type = "sine", volume = 0.04 }) {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return () => {};

    const context = new AudioContext();
    let isStopped = false;

    const playPattern = () => {
        if (isStopped) return;

        if (context.state === "suspended") {
            context.resume().catch(() => {});
        }

        const now = context.currentTime;
        pattern.forEach(({ frequency, duration, offset }) => {
            const oscillator = context.createOscillator();
            const gain = context.createGain();
            const startAt = now + offset;
            const stopAt = startAt + duration;

            oscillator.type = type;
            oscillator.frequency.value = frequency;
            gain.gain.setValueAtTime(0.0001, startAt);
            gain.gain.exponentialRampToValueAtTime(volume, startAt + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.0001, stopAt);

            oscillator.connect(gain).connect(context.destination);
            oscillator.start(startAt);
            oscillator.stop(stopAt + 0.04);
        });
    };

    playPattern();
    const timer = window.setInterval(playPattern, intervalMs);

    return () => {
        isStopped = true;
        window.clearInterval(timer);
        context.close().catch(() => {});
    };
}

function startIncomingCallTone() {
    if (!callTonesEnabled) return () => {};

    return startToneLoop({
        type: "triangle",
        volume: 0.045,
        intervalMs: 1500,
        pattern: [
            { frequency: 880, duration: 0.18, offset: 0 },
            { frequency: 660, duration: 0.18, offset: 0.26 },
        ],
    });
}

function startOutgoingCallTone() {
    if (!callTonesEnabled) return () => {};

    return startToneLoop({
        type: "sine",
        volume: 0.035,
        intervalMs: 2200,
        pattern: [
            { frequency: 440, duration: 0.22, offset: 0 },
            { frequency: 480, duration: 0.22, offset: 0.34 },
        ],
    });
}

const Dashboard = () => {
    const [user, setUser] = useState(null);
    const [contacts, setContacts] = useState([]);
    const [incomingRequests, setIncomingRequests] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [isSearchVisible, setIsSearchVisible] = useState(false);
    const [isSearchPerformed, setIsSearchPerformed] = useState(false);
    const [isCallOpen, setIsCallOpen] = useState(false);
    const [selectedContact, setSelectedContact] = useState(null);
    const [activeRoomId, setActiveRoomId] = useState("");
    const [isCallInitiator, setIsCallInitiator] = useState(false);
    const [incomingCall, setIncomingCall] = useState(null);
    const [outgoingCall, setOutgoingCall] = useState(null);
    const [incomingGroupCall, setIncomingGroupCall] = useState(null);
    const [activeGroupCall, setActiveGroupCall] = useState(null);
    const [selectedGroupParticipants, setSelectedGroupParticipants] = useState([]);
    const [isGroupCallOpen, setIsGroupCallOpen] = useState(false);
    const [callNotice, setCallNotice] = useState("");
    const [isAcceptingCall, setIsAcceptingCall] = useState(false);
    const [themeMode, setThemeMode] = useState(getInitialThemeMode);
    const [resolvedTheme, setResolvedTheme] = useState(() => resolveTheme(getInitialThemeMode()));
    const ringtoneStopRef = useRef(null);
    const ringbackStopRef = useRef(null);

    const stopRingtone = useCallback(() => {
        if (ringtoneStopRef.current) {
            ringtoneStopRef.current();
            ringtoneStopRef.current = null;
        }
    }, []);

    const stopRingback = useCallback(() => {
        if (ringbackStopRef.current) {
            ringbackStopRef.current();
            ringbackStopRef.current = null;
        }
    }, []);

    const startRingtone = useCallback(() => {
        if (!ringtoneStopRef.current) {
            ringtoneStopRef.current = startIncomingCallTone();
        }
    }, []);

    const startRingback = useCallback(() => {
        if (!ringbackStopRef.current) {
            ringbackStopRef.current = startOutgoingCallTone();
        }
    }, []);

    const findContact = useCallback((username) =>
        contacts.find((contact) => contact.username === username) || { username, confirmed: 1 },
    [contacts]);

    const openCallSession = useCallback((contact, roomId, isInitiator) => {
        stopRingtone();
        setSelectedContact(contact);
        setActiveRoomId(roomId);
        setIsCallInitiator(isInitiator);
        setIsCallOpen(true);
    }, [stopRingtone]);

    const closeCallSession = () => {
        setIsCallOpen(false);
        setSelectedContact(null);
        setActiveRoomId("");
        setIsCallInitiator(false);
    };

    const closeGroupCallSession = useCallback(() => {
        setIsGroupCallOpen(false);
        setActiveGroupCall(null);
    }, []);

    useEffect(() => () => {
        stopRingtone();
        stopRingback();
    }, [stopRingback, stopRingtone]);

    const cycleThemeMode = () => {
        setThemeMode((currentMode) => {
            const nextMode = themeModes[(themeModes.indexOf(currentMode) + 1) % themeModes.length];
            localStorage.setItem(THEME_STORAGE_KEY, nextMode);
            return nextMode;
        });
    };

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const userInfoResponse = await getUserInfo();
                setUser(userInfoResponse.data);
                setContacts(userInfoResponse.data.contacts || []);
            } catch (err) {
                console.error(err);
                window.location.href = appPath("/login");
            }
        };

        const fetchIncomingRequests = async () => {
            try {
                const incomingResponse = await getIncomingRequests();
                setIncomingRequests(incomingResponse.data.pending_requests || []);
            } catch (err) {
                console.error("Error fetching incoming requests:", err);
            }
        };

        fetchUserData();
        fetchIncomingRequests();
    }, []);

    useEffect(() => {
        const mediaQuery = window.matchMedia("(prefers-color-scheme: light)");
        const applyTheme = () => {
            const nextTheme = resolveTheme(themeMode);
            setResolvedTheme(nextTheme);
            document.documentElement.dataset.theme = nextTheme;
            document.documentElement.dataset.themeMode = themeMode;
        };

        applyTheme();
        mediaQuery.addEventListener("change", applyTheme);
        return () => mediaQuery.removeEventListener("change", applyTheme);
    }, [themeMode]);

    useEffect(() => {
        if (!user?.username) return;

        let isMounted = true;
        getIncomingCalls()
            .then(({ data }) => {
                logCallDebug("incoming-list", { count: data.length, calls: data });
                if (isMounted && data.length > 0) {
                    setIncomingCall(data[0]);
                }
            })
            .catch((err) => console.error("Error fetching incoming calls:", err));

        getIncomingGroupCalls()
            .then(({ data }) => {
                logCallDebug("incoming-group-list", { count: data.length, calls: data });
                if (isMounted && data.length > 0) {
                    setIncomingGroupCall(data[0]);
                }
            })
            .catch((err) => console.error("Error fetching incoming group calls:", err));

        const socket = new WebSocket(`${apiRoutes.CHAT_WS_URL}${apiRoutes.chat.notificationsSocket(user.username)}`);
        socket.onopen = () => logCallDebug("notification-ws-open", { user: user.username });
        socket.onmessage = (event) => {
            const message = JSON.parse(event.data);
            logCallDebug("notification", message);

            if (message.type === "incoming-call") {
                setIncomingCall(message);
                setCallNotice("");
            }

            if (message.type === "incoming-group-call") {
                setIncomingGroupCall(message);
                setCallNotice("");
            }

            if (message.type === "call-accepted") {
                setOutgoingCall(null);
                stopRingback();
                setCallNotice(`${message.from} accepted the call`);
                openCallSession(findContact(message.from), message.room_id, true);
            }

            if (message.type === "call-declined") {
                setOutgoingCall(null);
                stopRingback();
                setCallNotice(`${message.from} declined the call`);
            }

            if (message.type === "group-call-ended") {
                setCallNotice("Group call ended");
                closeGroupCallSession();
            }
        };
        socket.onerror = () => setCallNotice("Realtime notifications are disconnected");
        socket.onclose = (event) => logCallDebug("notification-ws-close", {
            code: event.code,
            reason: event.reason,
            user: user.username,
        });

        return () => {
            isMounted = false;
            socket.close();
        };
    }, [closeGroupCallSession, findContact, openCallSession, stopRingback, user?.username]);

    useEffect(() => {
        if (!outgoingCall?.call_id || isCallOpen) return undefined;

        const pollCallStatus = async () => {
            try {
                const { data } = await getContactCall(outgoingCall.call_id);
                if (data.status === "accepted") {
                    setOutgoingCall(null);
                    stopRingback();
                    setCallNotice(`${data.callee} accepted the call`);
                    openCallSession(findContact(data.callee), data.room_id, true);
                }
            } catch (err) {
                console.error("Error polling call status:", err);
            }
        };

        const timer = window.setInterval(pollCallStatus, 1500);
        pollCallStatus();
        return () => window.clearInterval(timer);
    }, [findContact, isCallOpen, openCallSession, outgoingCall?.call_id, stopRingback]);

    useEffect(() => {
        if ((incomingCall || incomingGroupCall) && !isCallOpen && !isGroupCallOpen) {
            startRingtone();
            return undefined;
        }

        stopRingtone();
        return undefined;
    }, [incomingCall, incomingGroupCall, isCallOpen, isGroupCallOpen, startRingtone, stopRingtone]);

    useEffect(() => {
        if (outgoingCall) {
            startRingback();
            return undefined;
        }

        stopRingback();
        return undefined;
    }, [outgoingCall, startRingback, stopRingback]);

    const handleSearch = async () => {
        setIsSearchPerformed(true);
        try {
            const { data } = await searchUsers(searchQuery);
            const filteredResults = data.filter((result) =>
                !contacts.some((contact) => contact.username === result.username)
            );
            setSearchResults(filteredResults);
        } catch (err) {
            console.error("Error during search:", err);
        }
    };

    const handleAddContact = async (contactUsername) => {
        try {
            await addContact(contactUsername);
            setSearchResults((prevResults) =>
                prevResults.filter((result) => result.username !== contactUsername)
            );
            setIsSearchVisible(false);
            const updatedUserInfo = await getUserInfo();
            setContacts(updatedUserInfo.data.contacts || []);
        } catch (err) {
            console.error("Error adding contact:", err);
        }
    };

    const handleRemoveContact = async (contactUsername) => {
        const shouldRemove = window.confirm(`Remove ${contactUsername} from your contacts?`);
        if (!shouldRemove) return;

        try {
            await removeContact(contactUsername);
            setContacts((currentContacts) =>
                currentContacts.filter((contact) => contact.username !== contactUsername)
            );
            setSearchResults((prevResults) =>
                prevResults.filter((result) => result.username !== contactUsername)
            );
        } catch (err) {
            console.error("Error removing contact:", err);
        }
    };

    const toggleGroupParticipant = (username) => {
        setSelectedGroupParticipants((current) => (
            current.includes(username)
                ? current.filter((item) => item !== username)
                : [...current, username].slice(0, 3)
        ));
    };

    const handleStartCall = async (contact) => {
        setCallNotice("");
        setOutgoingCall({ callee: contact.username, status: "Calling..." });
        startRingback();
        logCallDebug("start-request", { callee: contact.username });

        try {
            const { data } = await startContactCall(contact.username);
            logCallDebug("start-response", data);
            setOutgoingCall({
                call_id: data.call_id,
                room_id: data.room_id,
                callee: contact.username,
                status: data.delivered ? "Ringing" : "Ringing, contact may be offline",
            });
            openCallSession(contact, data.room_id, true);
        } catch (err) {
            console.error("Error starting call:", err.response?.data || err);
            setOutgoingCall(null);
            stopRingback();
            setCallNotice("Could not start the call");
        }
    };

    const handleStartGroupCall = async () => {
        if (selectedGroupParticipants.length === 0) {
            setCallNotice("Select at least one contact for a group call");
            return;
        }

        try {
            const { data } = await startGroupCall(selectedGroupParticipants, "Group call");
            setActiveGroupCall(data);
            setIsGroupCallOpen(true);
            setSelectedGroupParticipants([]);
            setCallNotice("Group call started");
        } catch (err) {
            console.error("Error starting group call:", err.response?.data || err);
            setCallNotice(`Could not start group call: ${err.response?.data?.detail || err.message}`);
        }
    };

    const handleAcceptCall = async () => {
        if (!incomingCall || isAcceptingCall) return;

        try {
            setIsAcceptingCall(true);
            logCallDebug("accept-request", incomingCall);
            const { data } = await acceptContactCall(incomingCall.call_id);
            logCallDebug("accept-response", data);
            setIncomingCall(null);
            stopRingtone();
            openCallSession(findContact(data.caller), data.room_id, false);
        } catch (err) {
            console.error("Error accepting call:", err.response?.data || err);
            setCallNotice(`Could not accept the call: ${err.response?.data?.detail || err.message}`);
        } finally {
            setIsAcceptingCall(false);
        }
    };

    const handleDeclineCall = async () => {
        if (!incomingCall) return;

        try {
            logCallDebug("decline-request", incomingCall);
            await declineContactCall(incomingCall.call_id);
            setIncomingCall(null);
            stopRingtone();
        } catch (err) {
            console.error("Error declining call:", err.response?.data || err);
            setIncomingCall(null);
        }
    };

    const handleAcceptGroupCall = async () => {
        if (!incomingGroupCall) return;

        try {
            const { data } = await joinGroupCall(incomingGroupCall.group_call_id);
            setIncomingGroupCall(null);
            stopRingtone();
            setActiveGroupCall(data);
            setIsGroupCallOpen(true);
        } catch (err) {
            console.error("Error accepting group call:", err.response?.data || err);
            setCallNotice(`Could not join group call: ${err.response?.data?.detail || err.message}`);
        }
    };

    const handleDeclineGroupCall = async () => {
        if (!incomingGroupCall) return;

        try {
            await declineGroupCall(incomingGroupCall.group_call_id);
            setIncomingGroupCall(null);
            stopRingtone();
        } catch (err) {
            console.error("Error declining group call:", err.response?.data || err);
            setIncomingGroupCall(null);
        }
    };

    if (!user) {
        return <div className="app-shell"><p className="loading-state">Loading...</p></div>;
    }

    return (
        <div className="app-shell">
            <aside className="contacts-panel">
                <div className="panel-header">
                    <div className="brand-lockup">
                        <img src="/azs-logo-square.svg" alt="" className="brand-logo" />
                        <div>
                        <span className="eyebrow">WebCRM</span>
                        <h1>Contacts</h1>
                        </div>
                    </div>
                    <div className="panel-actions">
                        <button className="theme-toggle" onClick={cycleThemeMode} title={`Theme: ${themeMode} (${resolvedTheme})`}>
                            {themeMode === "system" && <Monitor size={16} />}
                            {themeMode === "light" && <Sun size={16} />}
                            {themeMode === "dark" && <Moon size={16} />}
                        </button>
                        <span className="status-pill">Online</span>
                    </div>
                </div>

                <button className="primary-action" onClick={() => setIsSearchVisible(!isSearchVisible)}>
                    {isSearchVisible ? <X size={18} /> : <UserPlus size={18} />}
                    <span>{isSearchVisible ? "Close search" : "Add contact"}</span>
                </button>

                {isSearchVisible && (
                    <div className="search-card">
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(event) => setSearchQuery(event.target.value)}
                            placeholder="Search by username"
                        />
                        <button onClick={handleSearch}><Search size={16} /> Search</button>

                        <div className="search-results">
                            {isSearchPerformed && searchResults.length === 0 ? (
                                <p>No users found.</p>
                            ) : (
                                searchResults.map((result) => (
                                    <div className="search-result" key={result.id}>
                                        <span>{result.username}</span>
                                        <button onClick={() => handleAddContact(result.username)}>Add</button>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}

                <div className="contact-list">
                    {contacts.map((contact) => (
                        <div className="contact-row" key={contact.id || contact.username}>
                            <div className="avatar">{contact.username.slice(0, 1).toUpperCase()}</div>
                            <div className="contact-copy">
                                <strong>{contact.username}</strong>
                                <span>{contact.confirmed === 0 ? "Pending contact" : "Available"}</span>
                            </div>
                            <button className="icon-action" onClick={() => handleStartCall(contact)} title="Call">
                                <PhoneCall size={16} />
                            </button>
                            <button
                                className="icon-action is-danger"
                                onClick={() => handleRemoveContact(contact.username)}
                            >
                                <X size={16} />
                            </button>
                        </div>
                    ))}
                </div>

                <div className="group-call-card">
                    <div className="group-call-card-header">
                        <div>
                            <h2>Group call</h2>
                            <p>Select up to 3 contacts.</p>
                        </div>
                        <button
                            className="icon-action"
                            onClick={handleStartGroupCall}
                            disabled={selectedGroupParticipants.length === 0}
                            title="Start group call"
                        >
                            <Users size={16} />
                        </button>
                    </div>
                    <div className="group-contact-list">
                        {contacts.filter((contact) => contact.confirmed !== 0).map((contact) => (
                            <label className="group-contact-option" key={`group-${contact.id || contact.username}`}>
                                <input
                                    type="checkbox"
                                    checked={selectedGroupParticipants.includes(contact.username)}
                                    onChange={() => toggleGroupParticipant(contact.username)}
                                    disabled={!selectedGroupParticipants.includes(contact.username) && selectedGroupParticipants.length >= 3}
                                />
                                <span>{contact.username}</span>
                            </label>
                        ))}
                    </div>
                </div>

                <div className="requests-card">
                    <h2>Contact requests</h2>
                    {incomingRequests.length === 0 ? (
                        <p>No incoming requests.</p>
                    ) : (
                        <div className="request-list">
                            {incomingRequests.map((request) => (
                                <div className="request-row" key={request.id}>
                                    <span>{request.username}</span>
                                    <button onClick={() => handleAddContact(request.username)}>Accept</button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </aside>

            <main className={`call-stage ${isCallOpen || isGroupCallOpen ? "has-active-call" : ""}`}>
                {isGroupCallOpen ? (
                    <GroupCallSession
                        user={user}
                        groupCall={activeGroupCall}
                        isOpen={isGroupCallOpen}
                        onClose={closeGroupCallSession}
                    />
                ) : isCallOpen ? (
                    <WebRTCCallSession
                        user={user}
                        contact={selectedContact}
                        isOpen={isCallOpen}
                        onClose={closeCallSession}
                        autoStart
                        roomId={activeRoomId}
                        isInitiator={isCallInitiator}
                    />
                ) : (
                    <div className="stage-hero">
                        <span className="eyebrow">Realtime workspace</span>
                        <h2>Calls, chat, and screen sharing in one room.</h2>
                        <p>Select a contact and start a call. They will see an incoming call card and can answer when ready.</p>

                        {outgoingCall && (
                            <div className="outgoing-call-card">
                                <div className="avatar is-ringing">{outgoingCall.callee.slice(0, 1).toUpperCase()}</div>
                                <div>
                                    <strong>Calling {outgoingCall.callee}</strong>
                                    <span>{outgoingCall.status}</span>
                                </div>
                            </div>
                        )}

                        {callNotice && <p className="call-notice">{callNotice}</p>}
                    </div>
                )}
            </main>

            {incomingCall && (
                <div className="incoming-call-card">
                    <div className="avatar is-ringing">{incomingCall.from.slice(0, 1).toUpperCase()}</div>
                    <div className="incoming-copy">
                        <span>Incoming voice call</span>
                        <strong>{incomingCall.from}</strong>
                    </div>
                    <div className="incoming-actions">
                        <button className="accept-call" onClick={handleAcceptCall} disabled={isAcceptingCall}><Phone size={18} /> Answer</button>
                        <button className="decline-call" onClick={handleDeclineCall}><PhoneOff size={18} /> Decline</button>
                    </div>
                </div>
            )}

            {incomingGroupCall && (
                <div className="incoming-call-card">
                    <div className="avatar is-ringing"><Users size={18} /></div>
                    <div className="incoming-copy">
                        <span>Incoming group call</span>
                        <strong>{incomingGroupCall.from}</strong>
                    </div>
                    <div className="incoming-actions">
                        <button className="accept-call" onClick={handleAcceptGroupCall}><Phone size={18} /> Join</button>
                        <button className="decline-call" onClick={handleDeclineGroupCall}><PhoneOff size={18} /> Decline</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Dashboard;
